import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import Content from '@/components/foundations/content';
import SwiperCore, { Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import * as styles from './plan.module.scss';

import PlanningSvg from '@/svgs/output/planning';
import ScenarioSvg from '@/svgs/output/scenario';
import AnalysisSvg from '@/svgs/output/analysis';
import WireFrameSvg from '@/svgs/output/wireFrame';
import DesignSvg from '@/svgs/output/design';
import InspectionSvg from '@/svgs/output/inspection';
import GoodSvg from '@/svgs/icon/good';

const PlanContent: React.FC = () => {
  const [windowSize, setWindowSize] = useState({
    width: 1200,
    height: 800,
  });

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize);
    updateWindowSize();

    return () => {
      window.removeEventListener('resize', updateWindowSize);
    };
  }, []);

  const updateWindowSize = () => {
    setWindowSize({ width: window.innerWidth, height: window.innerHeight });
  };

  const noticeStyle = classNames(styles.oNotice, 'o-notice');

  return (
    <>
      {' '}
      <h2>
        <span>
          <strong>Plan</strong>
          <small>価格・納期</small>
        </span>
      </h2>
      <h3>最短の期間で、最適なプロトタイプのアウトプットをご提供いたします。</h3>
      {windowSize.width >= 1000 ? (
        <ul className={styles.pPlan__list}>
          <li className={styles.basic}>
            <CompactPlan />
            <BasicPlan />
            <AdvancedPlan />
          </li>
          <li className={styles.custom}>
            <CustomPlan />
          </li>
        </ul>
      ) : (
        <SmallPlanComponent />
      )}
      <ul className={noticeStyle}>
        <li>
          上記プランには、開発が必要なプロトタイプは含んでおりません。
          <br />
          開発を伴うプロトタイプ制作をご希望の場合は、カスタムプランにて個別にご相談をお受けしております。
        </li>
        <li>プランは一例です。成果物や価格・納期に関しましては是非ご相談ください。</li>
      </ul>
    </>
  );
};

const SmallPlanComponent: React.FC = () => {
  SwiperCore.use([Autoplay]);

  return (
    <Swiper slidesPerView={'auto'} loop loopedSlides={2} centeredSlides className={styles.pPlan__swipe}>
      <SwiperSlide className={styles.pPlan__swipe__slide}>
        <CompactPlan />
      </SwiperSlide>
      <SwiperSlide className={styles.pPlan__swipe__slide}>
        <BasicPlan />
      </SwiperSlide>
      <SwiperSlide className={styles.pPlan__swipe__slide}>
        <AdvancedPlan />
      </SwiperSlide>
      <SwiperSlide className={`${styles.custom} ${styles.pPlan__swipe__slide}`}>
        <CustomPlan />
      </SwiperSlide>
    </Swiper>
  );
};

const CompactPlan: React.FC = () => {
  return (
    <div className={styles.oPrice}>
      <h4 className={styles.oPrice__title}>コンパクト</h4>
      <p className={styles.oPrice__tagline}>
        アイデアを見える化
        <br />
        クイックな検証に最適です
      </p>
      <div className={styles.oPrice__delivery}>最短3週間〜</div>
      <div className={styles.oPrice__price}>&yen;800,000〜</div>
      <ul className={styles.oPrice__output}>
        <li>
          プロジェクト計画書
          <PlanningSvg />
          <small className={styles.oPrice__output__simple}>簡易</small>
        </li>
        <li>
          ユーザーシナリオ定義
          <ScenarioSvg />
          <small className={styles.oPrice__output__simple}>簡易</small>
        </li>
        <li>
          <del>分析設計書</del>
          <AnalysisSvg />
        </li>
        <li>
          ペーパープロト / ワイヤー
          <WireFrameSvg />
        </li>
        <li>
          <del>デザインモックアップ</del>
          <DesignSvg />
        </li>
        <li>
          <del>検証結果分析</del>
          <InspectionSvg />
        </li>
      </ul>
      <dl className={styles.oPrice__recommend}>
        <dt>こんな方におすすめ</dt>
        <dd>
          <ul>
            <li>お試しでお願いしたい</li>
            <li>最小限の検証がしたい</li>
            <li>プレゼン用資料として</li>
          </ul>
        </dd>
      </dl>
    </div>
  );
};

const BasicPlan: React.FC = () => {
  return (
    <div className={styles.oPrice}>
      <h4 className={styles.oPrice__title}>ベーシック</h4>
      <p className={styles.oPrice__tagline}>
        検証結果をフィードバック
        <br />
        計画精度を高めるのに最適です
      </p>
      <div className={styles.oPrice__delivery}>平均1.5ヶ月〜</div>
      <div className={styles.oPrice__price}>&yen;1,500,000〜</div>
      <ul className={styles.oPrice__output}>
        <li>
          プロジェクト計画書
          <PlanningSvg />
        </li>
        <li>
          ユーザーシナリオ定義
          <ScenarioSvg />
          <small className={styles.oPrice__output__simple}>簡易</small>
        </li>
        <li>
          分析設計書
          <AnalysisSvg />
        </li>
        <li>
          <WireFrameSvg />
          ペーパープロト / ワイヤー
        </li>
        <li>
          デザインモックアップ
          <DesignSvg />
          <small className={styles.oPrice__output__simple}>簡易</small>
        </li>
        <li>
          検証結果分析
          <InspectionSvg />
          <small className={styles.oPrice__output__simple}>簡易</small>
        </li>
      </ul>
      <dl className={styles.oPrice__recommend}>
        <dt>こんな方におすすめ</dt>
        <dd>
          <ul>
            <li>標準・推奨を知りたい</li>
            <li>ある程度お任せしたい</li>
            <li>バランスを重視したい</li>
          </ul>
        </dd>
      </dl>
    </div>
  );
};

const AdvancedPlan: React.FC = () => {
  return (
    <div className={styles.oPrice}>
      <h4 className={styles.oPrice__title}>アドバンスド</h4>
      <p className={styles.oPrice__tagline}>
        導線設計やユーザー心理を
        <br />
        じっくり検証するのに最適です
      </p>
      <div className={styles.oPrice__delivery}>納期は応相談</div>
      <div className={styles.oPrice__price}>&yen;3,000,000〜</div>
      <ul className={styles.oPrice__output}>
        <li>
          プロジェクト計画書
          <PlanningSvg />
        </li>
        <li>
          ユーザーシナリオ定義
          <ScenarioSvg />
        </li>
        <li>
          分析設計書
          <AnalysisSvg />
        </li>
        <li>
          ペーパープロト / ワイヤー
          <WireFrameSvg />
        </li>
        <li>
          デザインモックアップ
          <DesignSvg />
        </li>
        <li>
          検証結果分析
          <InspectionSvg />
        </li>
      </ul>
      <dl className={styles.oPrice__recommend}>
        <dt>こんな方におすすめ</dt>
        <dd>
          <ul>
            <li>細部までこだわりたい</li>
            <li>多パターンを試したい</li>
            <li>詳細な成果物が欲しい</li>
          </ul>
        </dd>
      </dl>
    </div>
  );
};

const CustomPlan: React.FC = () => {
  return (
    <div className={styles.oPrice}>
      <h4 className={styles.oPrice__title}>カスタム</h4>
      <p className={styles.oPrice__tagline}>
        ご相談内容に合わせて
        <br />
        お客様と伴走いたします
      </p>
      <div className={styles.oPrice__delivery}>納期は応相談</div>
      <div className={styles.oPrice__price}>&yen;ASK</div>
      <ul className={styles.oPrice__output}>
        <li>
          プロジェクト計画書
          <PlanningSvg />
        </li>
        <li>
          ユーザーシナリオ定義
          <ScenarioSvg />
        </li>
        <li>
          分析設計書
          <AnalysisSvg />
        </li>
        <li>
          ペーパープロト / ワイヤー
          <WireFrameSvg />
        </li>
        <li>
          デザインモックアップ
          <DesignSvg />
        </li>
        <li>
          検証結果分析
          <InspectionSvg />
        </li>
        <li className={styles.extra}>
          <p>
            <GoodSvg />
            提案活動、開発、概念実証…
            <br />
            幅広く対応可能です。
            <br />
            まずはご相談ください。
          </p>
        </li>
      </ul>
      <dl className={styles.oPrice__recommend}>
        <dt>こんな方におすすめ</dt>
        <dd>
          <ul>
            <li>開発までお願いしたい</li>
            <li>システムが前提にある</li>
            <li>大規模な依頼をしたい</li>
          </ul>
        </dd>
      </dl>
    </div>
  );
};

const Plan: React.FC = () => {
  return (
    <Content
      className={{ wrapper: styles.pPlan, inner: styles.lContent__inner }}
      intersection={{ visible: styles.visible, standby: styles.standby, threshold: 0.3 }}
    >
      <PlanContent />
    </Content>
  );
};

export default Plan;
